import { Link, useLocation, useSearchParams } from "react-router-dom";
import { LayoutDashboard, User, Heart, HeartHandshake, BookOpen, FolderKanban, ChevronDown, Command, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLastViewedStudent } from "@/hooks/useLastViewedStudent";

/**
 * Each role preserves the shared grade/house/term filters via URL search params.
 * Some personas add persona-appropriate defaults (e.g. Counsellor focuses on the
 * current term) so the presenter lands in a well-scoped view.
 */
const roles: {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  defaults?: Record<string, string>;
}[] = [
  { to: "/schools",           label: "Principal",  icon: LayoutDashboard, end: true },
  { to: "/schools/student",   label: "Student",    icon: User },
  { to: "/schools/parent",    label: "Parent",     icon: Heart },
  { to: "/schools/counsellor",label: "Counsellor", icon: HeartHandshake, defaults: { term: "current" } },
  { to: "/schools/studios",   label: "Studios",    icon: BookOpen },
  { to: "/schools/portfolio", label: "Portfolio",  icon: FolderKanban },
];

const PRESERVED_KEYS = ["grade", "house", "term"] as const;

// Persona → printable one-pager. Surfaced as a quick action inside the switcher
// so presenters can jump to a print-ready view without leaving the persona.
const ONE_PAGERS: Record<string, { to: string; label: string }> = {
  "/schools/student": { to: "/schools/student/onepager", label: "Learner one-pager" },
  "/schools/parent": { to: "/schools/parent/onepager", label: "Family one-pager" },
  "/schools/counsellor": { to: "/schools/counsellor/onepager", label: "Weekly brief" },
};

export default function RoleSwitcher() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const lastStudent = useLastViewedStudent();
  const active =
    roles.find((r) => (r.end ? pathname === r.to : pathname.startsWith(r.to))) ?? roles[0];
  const ActiveIcon = active.icon;

  const buildTo = (r: (typeof roles)[number]) => {
    const next = new URLSearchParams();
    PRESERVED_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) next.set(k, v);
    });
    if (r.defaults) {
      Object.entries(r.defaults).forEach(([k, v]) => {
        if (!next.has(k)) next.set(k, v);
      });
    }
    // Deep-link the Student role back to the last-viewed learner so the
    // presenter never lands on an empty state after switching roles.
    if (r.to === "/schools/student" && lastStudent && !next.has("student")) {
      next.set("student", lastStudent);
    }
    const qs = next.toString();
    return qs ? `${r.to}?${qs}` : r.to;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5" aria-label="Switch role">
          <ActiveIcon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Viewing as {active.label}</span>
          <span className="sm:hidden">{active.label}</span>
          <ChevronDown className="w-3 h-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
          Jump to role
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {roles.map((r) => {
          const Icon = r.icon;
          const isActive = active.to === r.to;
          return (
            <DropdownMenuItem key={r.to} asChild>
              <Link
                to={buildTo(r)}
                className="flex items-center gap-2"
                aria-current={isActive ? "page" : undefined}
              >
                <Icon className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                <span className="flex-1">{r.label}</span>
                {r.to === "/schools/student" && lastStudent && !isActive && (
                  <span className="text-[10px] text-muted-foreground" aria-label="Resumes last-viewed student">
                    resume
                  </span>
                )}
                {isActive && <span className="text-[10px] text-primary" aria-hidden="true">•</span>}
              </Link>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
          Print
        </DropdownMenuLabel>
        {ONE_PAGERS[active.to] && (
          <DropdownMenuItem asChild>
            <Link to={ONE_PAGERS[active.to].to} className="flex items-center gap-2">
              <Printer className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
              <span className="flex-1">{ONE_PAGERS[active.to].label}</span>
              <span className="text-[10px] text-muted-foreground">A4</span>
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            window.print();
          }}
          className="flex items-center gap-2"
        >
          <Printer className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          <span className="flex-1">Print current view</span>
          <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-foreground">
            ⌘P
          </kbd>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 flex items-center justify-between text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Command className="w-3 h-3" aria-hidden="true" /> Search
          </span>
          <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-foreground">
            ⌘K
          </kbd>
        </div>

      </DropdownMenuContent>
    </DropdownMenu>
  );
}
